import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  MenuItem
} from '@mui/material';
import { ArrowBack, Save, ExpandMore, Book } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const AddWeeklyPlan = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classData, setClassData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [weeklyPlans, setWeeklyPlans] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedSubject, setExpandedSubject] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch class data
        const classResponse = await teacherAPI.getClasses();
        const classes = classResponse.data.classes || [];
        const currentClass = classes.find(c => c.id === id);
        setClassData(currentClass);
        
        // Fetch subjects for this class
        const subjectsResponse = await teacherAPI.getClassSubjects(id);
        setSubjects(subjectsResponse.data.subjects || []);
        
        // TODO: Fetch existing weekly plans
        // const plansResponse = await teacherAPI.getWeeklyPlans(id);
        // Initialize plans state with existing data
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const handleAccordionChange = (subjectId) => (event, isExpanded) => {
    setExpandedSubject(isExpanded ? subjectId : null);
  };

  const handlePlanChange = (subjectId, field, value) => {
    setWeeklyPlans(prev => ({
      ...prev,
      [subjectId]: {
        ...prev[subjectId],
        [field]: value
      }
    }));
    setError('');
  };

  const handleSavePlan = async (subjectId) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const plan = weeklyPlans[subjectId];
      
      if (!selectedWeek) {
        setError('Please select a week first');
        setSaving(false);
        return;
      }
      
      if (!plan || !plan.topic) {
        setError('Please fill in Topic field');
        setSaving(false);
        return;
      }

      const planData = {
        subject_id: subjectId,
        week: selectedWeek,
        topic: plan.topic,
        objectives: plan.objectives || ''
      };

      // Save weekly plan via API
      await teacherAPI.saveWeeklyPlan(id, planData);
      
      const subject = subjects.find(s => s.id === subjectId);
      setSuccess(`Weekly plan saved successfully for ${subject?.subject_name}`);
      
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save weekly plan');
    } finally {
      setSaving(false);
    }
  };

  const getWeekOptions = () => {
    const weeks = [];
    const currentYear = new Date().getFullYear();
    const startOfYear = new Date(currentYear, 0, 1); // January 1st
    
    for (let i = 1; i <= 52; i++) {
      const weekStartDate = new Date(startOfYear);
      weekStartDate.setDate(startOfYear.getDate() + (i - 1) * 7);
      
      const weekEndDate = new Date(weekStartDate);
      weekEndDate.setDate(weekStartDate.getDate() + 6);
      
      const formatDate = (date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()}`;
      };
      
      weeks.push({
        number: i,
        label: `Week ${i} (${formatDate(weekStartDate)} - ${formatDate(weekEndDate)})`
      });
    }
    return weeks;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Add Weekly Plan
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {classData?.name} {classData?.grade && `• Grade ${classData.grade}`} {classData?.section && `• Section ${classData.section}`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/teacher/classes/${id}/manage`)}
        >
          Back
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {subjects.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="h6">
            No subjects added yet
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Please add subjects before creating weekly plans
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate(`/teacher/classes/${id}/subjects`)}
          >
            Add Subjects
          </Button>
        </Paper>
      ) : (
        <>
          {/* Week Selector at the top */}
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.lighter' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  select
                  label="Select Week for All Subjects"
                  value={selectedWeek}
                  onChange={(e) => {
                    setSelectedWeek(e.target.value);
                    setError('');
                  }}
                  helperText="This week will apply to all subjects below"
                  sx={{ bgcolor: 'white' }}
                >
                  {getWeekOptions().map((week) => (
                    <MenuItem key={week.number} value={week.number}>
                      {week.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                {selectedWeek && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip 
                      label={`Week ${selectedWeek} Selected`}
                      color="primary"
                      size="medium"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Create plans for all subjects for this week
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="body1" color="text.secondary">
              Total Subjects: {subjects.length}
            </Typography>
          </Box>

          {subjects.map((subject) => (
            <Accordion
              key={subject.id}
              expanded={expandedSubject === subject.id}
              onChange={handleAccordionChange(subject.id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`subject-${subject.id}-content`}
                id={`subject-${subject.id}-header`}
              >
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Book color="primary" />
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight="bold">
                      {subject.subject_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Code: {subject.subject_code}
                    </Typography>
                  </Box>
                  {weeklyPlans[subject.id]?.topic && (
                    <Chip 
                      label="Plan Created"
                      size="small"
                      color="success"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Create Weekly Plan
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Topic *"
                        value={weeklyPlans[subject.id]?.topic || ''}
                        onChange={(e) => handlePlanChange(subject.id, 'topic', e.target.value)}
                        placeholder="Main topic for the week"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Learning Objectives"
                        value={weeklyPlans[subject.id]?.objectives || ''}
                        onChange={(e) => handlePlanChange(subject.id, 'objectives', e.target.value)}
                        placeholder="What students should learn by the end of the week"
                      />
                    </Grid>
                  </Grid>

                  <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                    <Button
                      variant="outlined"
                      onClick={() => handlePlanChange(subject.id, 'all', {})}
                    >
                      Clear Form
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                      onClick={() => handleSavePlan(subject.id)}
                      disabled={saving}
                    >
                      Save Weekly Plan
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </Container>
  );
};

export default AddWeeklyPlan;
